import MEGA_CLIENT from './client'
import type { ElectricConnection, DeviceContact } from '../types/connections'

export async function getConnections(schemaId: number): Promise<ElectricConnection[]> {
  try {
    const response = await MEGA_CLIENT.get('/api/connections', {
      params: { schema_id: schemaId }
    })
    if (Array.isArray(response.data)) {
      return response.data
    }
    console.warn('⚠️ Connections response invalid or truncated (proxy/network?), using empty list');
    return []
  } catch (error) {
    console.error('❌ Error fetching connections:', error);
    return []
  }
}

export async function createConnection(params: {
  schema_id: number
  from_instance_id: number
  from_contact_id?: number
  to_instance_id: number
  to_contact_id?: number
  from_interface_id?: number
  to_interface_id?: number
  connection_type?: string
  wire_type?: string
  wire_properties?: Record<string, any>
  path_points?: { x: number; y: number }[]
  is_manual_route?: boolean
}): Promise<ElectricConnection> {
  
  const response = await MEGA_CLIENT.post('/api/connections', params)
  

  
  // After interceptor fix, response.data contains the actual data
  if (response.data && typeof response.data === 'object' && 'id' in response.data) {
    return response.data as ElectricConnection
  }
  
  throw new Error('Failed to create connection: Invalid response format')
}

export async function updateConnection(
  connectionId: number,
  params: {
    wire_properties?: Record<string, any>
    path_points?: { x: number; y: number }[]
    is_manual_route?: boolean
    /** false = use visible_on_schema_ids; true = legacy (show on all schemas) */
    visibility_legacy?: boolean
    /** Schema IDs where connection is visible in addition to home (schema_id). Used when visibility_legacy is false. */
    visible_on_schema_ids?: number[]
    /** Home schema for this connection (where the line "lives"). Changing it migrates the line to another schema. */
    schema_id?: number
  }
): Promise<void> {
  
  await MEGA_CLIENT.put(`/api/connections/${connectionId}`, params)
  
  
  // API client interceptor handles success response format
  // If no error thrown, operation was successful
}

export async function deleteConnection(connectionId: number): Promise<void> {
  
  await MEGA_CLIENT.delete(`/api/connections/${connectionId}`)
  
  
  // API client interceptor handles success response format
  // If no error thrown, operation was successful
}

export async function getDeviceConnections(
  deviceInstanceId: number, 
  schemaId: number
): Promise<ElectricConnection[]> {
  try {
    const response = await MEGA_CLIENT.get('/api/connections', {
      params: { 
        schema_id: schemaId,
        device_instance_id: deviceInstanceId 
      }
    })
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    console.warn('⚠️ Device connections response invalid or truncated, using empty list');
    return []
  } catch (error) {
    console.error('❌ Error fetching device connections:', error);
    return []
  }
}

export async function deleteDeviceConnections(
  deviceInstanceId: number, 
  schemaId: number
): Promise<void> {
  try {
    // Get all connections for this device in this schema
    const connections = await getDeviceConnections(deviceInstanceId, schemaId);
    
    // Delete each connection
    const deletePromises = connections.map(connection => 
      deleteConnection(connection.id)
    );
    
    await Promise.all(deletePromises);
    
    console.log(`✅ Deleted ${connections.length} connections for device ${deviceInstanceId} in schema ${schemaId}`);
  } catch (error) {
    console.error('❌ Error deleting device connections:', error);
    throw error;
  }
}

export async function getDeviceContacts(specificationId: number, schemaType?: string, schemaId?: number): Promise<DeviceContact[]> {
  
  const params: any = { specification_id: specificationId }
  // Приоритет: schemaId (новая логика) > schemaType (legacy)
  if (schemaId) {
    params.schema_id = schemaId
  } else if (schemaType) {
    params.schema_type = schemaType
  }
  
  const response = await MEGA_CLIENT.get('/api/device_contacts', { params })
  

  
  // After interceptor fix, response.data contains the actual data
  if (Array.isArray(response.data)) {
    return response.data
  }
  
  throw new Error('Failed to fetch device contacts: Invalid response format')
}



export async function createDeviceContact(params: {
  specification_id: number
  contact_number: number | string  // Support TOP/BOTTOM cable contacts
  contact_label?: string
  color_code?: string
  contact_position?: 'top' | 'bottom' | 'left' | 'right'
  properties?: Record<string, any>
  schema_type_ids?: number[]  // LEGACY: Array of schema type IDs
  schema_ids?: number[]  // NEW: Array of specific schema IDs
}): Promise<DeviceContact> {
  
  const response = await MEGA_CLIENT.post('/api/device_contacts', params)
  

  
  // After interceptor fix, response.data contains the actual data
  if (response.data && typeof response.data === 'object' && 'id' in response.data) {
    return response.data as DeviceContact
  }
  
  throw new Error('Failed to create device contact: Invalid response format')
}

export async function updateDeviceContact(contactId: number, params: {
  contact_number?: number | string  // Support TOP/BOTTOM cable contacts
  contact_label?: string
  color_code?: string
  properties?: Record<string, any>
  schema_type_ids?: number[]  // LEGACY: Array of schema type IDs
  schema_ids?: number[]  // NEW: Array of specific schema IDs
}): Promise<DeviceContact> {
  
  const response = await MEGA_CLIENT.put(`/api/device_contacts/${contactId}`, params)
  

  
  // After interceptor fix, response.data contains the actual data
  if (response.data && typeof response.data === 'object' && 'id' in response.data) {
    return response.data as DeviceContact
  }
  
  // If response indicates success but no data returned, create a mock object
  return {
    id: contactId,
    specification_id: 0,
    contact_number: params.contact_number || '',
    contact_label: params.contact_label || null,
    color_code: params.color_code || null,
    properties: params.properties || null
  } as DeviceContact
}

export async function deleteDeviceContact(contactId: number): Promise<void> {
  await MEGA_CLIENT.delete(`/api/device_contacts/${contactId}`)
  
  // API client interceptor handles success response format
  // If no error thrown, operation was successful
}

// Interface for cable to terminal connections data
export interface CableToTerminalConnection {
  cable_instance_id: number
  cable_contact_id: number
  cable_position_label: string
  cable_device_type?: string
  cable_position_x: number
  cable_position_y: number
  terminal_instance_id: number
  terminal_contact_id: number
  terminal_opposite_contact_id?: number
  terminal_block_id: number
  terminal_block_name: string
  terminal_block_description?: string
  terminal_position_in_block: number
  terminal_specification_id: number
  terminal_contact_label?: string
  terminal_opposite_contact_label?: string
  connection_id: number
  source_schema_id: number
  cable_contact_number: string
}

// Get connections between cables and terminal blocks for auto-placement
export async function getCableToTerminalConnections(schemaId: number): Promise<CableToTerminalConnection[]> {
  try {
    const response = await MEGA_CLIENT.get('/api/cable-terminal-connections', {
      params: { schema_id: schemaId }
    })
    if (Array.isArray(response.data)) {
      return response.data
    }
    console.warn('⚠️ Cable-terminal connections response invalid or truncated (proxy/network?), using empty list');
    return []
  } catch (error) {
    console.error('❌ Error fetching cable to terminal connections:', error);
    return []
  }
}